/**
 * Dá para instalar o app agora? E como?
 *
 * Até aqui a plataforma não capturava `beforeinstallprompt` e dependia do
 * convite **nativo** do navegador. Isso funciona na primeira visita e falha
 * exatamente no caso relatado: **depois de desinstalar, o convite não volta**.
 * O Chrome estrangula a barrinha automática por heurística própria (engajamento
 * recente, dispensa anterior, desinstalação), e nós não temos como pedir que
 * ele reapareça.
 *
 * O evento, porém, continua sendo disparado enquanto o site for instalável e
 * não estiver instalado. Capturá-lo e oferecer o nosso próprio botão é o que
 * torna a instalação alcançável sempre — não é um atalho para o convite do
 * navegador, é o único caminho que nós controlamos.
 *
 * ## iPhone é outro caminho, não um caso de borda
 *
 * O Safari **não implementa** `beforeinstallprompt`. Lá a instalação é manual
 * (Compartilhar → "Adicionar à Tela de Início") e nenhum código a dispara.
 * Por isso o modo `"ios"`: sem ele, metade da equipe veria uma tela que nunca
 * oferece nada, e a conclusão seria "o app não instala no iPhone".
 */
import { useEffect, useState } from "react";

/** O evento do Chrome, que não está no lib.dom. */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export type ModoDeInstalacao =
  /** Nada a oferecer: já está instalado, ou o navegador não instala. */
  | { modo: "indisponivel" }
  /** Chrome e similares: temos o evento guardado e podemos abrir o convite. */
  | { modo: "prompt"; instalar: () => Promise<boolean> }
  /** iPhone: só instruções, porque não há API. */
  | { modo: "ios" };

/** Já está rodando como app instalado? */
export function rodandoInstalado(): boolean {
  try {
    if (window.matchMedia("(display-mode: standalone)").matches) return true;
    // iOS não implementa `display-mode: standalone` em versões antigas.
    return (window.navigator as { standalone?: boolean }).standalone === true;
  } catch {
    return false;
  }
}

function eIOSComSafari(): boolean {
  try {
    const ua = window.navigator.userAgent;
    const ios = /iPad|iPhone|iPod/.test(ua)
      // iPad moderno se declara Mac; o toque é o que o distingue.
      || (ua.includes("Macintosh") && navigator.maxTouchPoints > 1);
    if (!ios) return false;
    // Dentro do iOS todo navegador é WebKit, mas só o Safari oferece
    // "Adicionar à Tela de Início" — Chrome e Firefox no iPhone não têm.
    return !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
  } catch {
    return false;
  }
}

export function useInstalavel(): ModoDeInstalacao {
  const [evento, setEvento] = useState<BeforeInstallPromptEvent | null>(null);
  const [instalado, setInstalado] = useState(() => rodandoInstalado());

  useEffect(() => {
    const aoPoderInstalar = (e: Event) => {
      // Sem o `preventDefault` o Chrome ainda pode mostrar a barrinha dele
      // por cima do nosso convite — dois pedidos para a mesma coisa.
      e.preventDefault();
      setEvento(e as BeforeInstallPromptEvent);
    };

    // **Esconde o convite assim que instala, sem recarregar.** Sem isto o
    // botão fica na tela depois de a pessoa aceitar, e parece que falhou.
    const aoInstalar = () => {
      setInstalado(true);
      setEvento(null);
    };

    window.addEventListener("beforeinstallprompt", aoPoderInstalar);
    window.addEventListener("appinstalled", aoInstalar);
    return () => {
      window.removeEventListener("beforeinstallprompt", aoPoderInstalar);
      window.removeEventListener("appinstalled", aoInstalar);
    };
  }, []);

  if (instalado) return { modo: "indisponivel" };

  if (evento) {
    return {
      modo: "prompt",
      instalar: async () => {
        await evento.prompt();
        const { outcome } = await evento.userChoice;
        // **O evento é de uso único.** Reusá-lo levanta
        // "The prompt() method may only be called once". Descartando aqui, o
        // botão some; se a pessoa recusar, o Chrome dispara o evento de novo
        // numa visita seguinte e o botão volta sozinho.
        setEvento(null);
        return outcome === "accepted";
      },
    };
  }

  if (eIOSComSafari()) return { modo: "ios" };

  return { modo: "indisponivel" };
}
